import React, { useEffect } from 'react';
import { useI18n } from '../../i18n/I18nProvider';
import { endImpersonation, getImpersonationExpiry, getImpersonationTarget } from '../../shared/api/impersonation';
import './ImpersonationBanner.css';
import { formatAppTime } from '../../shared/utils/dates';

/**
 * The strip shown while a super admin sees Kudos as someone else: whose view it is,
 * when it ends, and the way back. The session ends on its own when time runs out.
 */
export default function ImpersonationBanner({ fallbackName }: { fallbackName?: string }) {
  const { t } = useI18n();
  const target = getImpersonationTarget();
  const expiry = getImpersonationExpiry();
  const name = target?.name || fallbackName || target?.email || t('impersonation.someone');
  const expiresAtMs = expiry ? expiry.getTime() : null;

  useEffect(() => {
    if (expiresAtMs == null) return undefined;
    const left = expiresAtMs - Date.now();
    if (left <= 0) {
      endImpersonation('expired');
      return undefined;
    }
    const timer = window.setTimeout(() => endImpersonation('expired'), left);
    return () => window.clearTimeout(timer);
  }, [expiresAtMs]);

  const until = expiry
    ? formatAppTime(expiry)
    : null;

  return (
    <div className="imp-strip" role="status">
      <span className="imp-strip__text">
        <strong>{t('impersonation.seeingAs', { name })}</strong>
        {until && <span className="imp-strip__until">{t('impersonation.until', { time: until })}</span>}
        <span className="imp-strip__note">{t('impersonation.limited')}</span>
      </span>
      <button type="button" className="imp-strip__back" onClick={() => endImpersonation('ended')}>
        {t('impersonation.back')}
      </button>
    </div>
  );
}
